// ============================================================================
// HR Onboarding — data hooks. Flat-fetch + IN-lookup + client-join, matching
// the house style (see src/modules/hr/hooks/use-hr-data.ts). No TanStack Query.
//
// RLS (verified 2026-05-28): hr + superuser have full UPDATE on both
// onboarding tables and service_providers; hr/exec/finance/superuser + every
// AM role have SELECT. So reads work for everyone with access; writes only
// land for hr / superuser (others get a graceful RLS-denied toast + rollback).
// ============================================================================
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { commitWrite } from '../../../shared/save-path';
import {
  mapStatusToStage,
  parseChecklist,
  computeProgress,
  effectiveChecklist,
  coiTrafficLight,
} from '../utils';
import type { OnboardingRecord, OnboardingType } from '../types';
import { deriveCapabilities, type Capability } from '../../subcontractors/capabilities';
import { errorMessage } from '../../../shared/errors';
import { fetchAmNameMap } from '../../../shared/am-name';
import { catalogLabel } from '../../../shared/service-roles';

// ---------------------------------------------------------------------------
// Shared join helpers
// ---------------------------------------------------------------------------
async function fetchMap<T extends Record<string, unknown>>(
  table: string,
  idCol: string,
  ids: string[],
  cols: string,
): Promise<Record<string, T>> {
  if (ids.length === 0) return {};
  const res = await supabase.from(table).select(cols).in(idCol, ids);
  if (res.error || !res.data) return {};
  const out: Record<string, T> = {};
  for (const row of res.data as unknown as T[]) {
    out[String(row[idCol])] = row;
  }
  return out;
}

// partner_onboarding rows are subcontractor companies.
// 2026-06-02 (Antonela H3): Partner and Subcontractor are the same category, so
// everything that isn't a W2 employee resolves to 'Subcontractor'. The 'Partner'
// OnboardingType value is retained only for back-compat of existing checklist
// applies_to arrays; nothing maps a record to it anymore.
function partnerTypeToOnboardingType(_partnerType: string | null): OnboardingType {
  return 'Subcontractor';
}

// What the card shows under the name. The stored class is a key, never a word a
// person should read, so it is turned into one here.
function payeeClassLabel(workerClass: string | null): string {
  if (workerClass === 'individual_1099') return '1099 individual';
  return 'Subcontractor';
}

function empTypeToOnboardingType(empType: string | null): OnboardingType {
  if (!empType) return 'W2';
  if (/sub|partner|1099/i.test(empType)) return 'Subcontractor';
  return 'W2';
}

// ---------------------------------------------------------------------------
// useOnboardingRecords — UNION of employee_onboarding + partner_onboarding.
// ---------------------------------------------------------------------------
export interface UseOnboardingResult {
  records: OnboardingRecord[];
  /** employee_id -> display name, for the optional named assignee (TCSD-0162). */
  ownerNames: Map<string, string>;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  // Generic optimistic field patch. dbPatch is written to the source table;
  // localPatch transforms the in-memory record. Rolls back on RLS denial.
  patchRecord: (
    rec: OnboardingRecord,
    dbPatch: Record<string, unknown>,
    localPatch: Partial<OnboardingRecord>,
  ) => Promise<void>;
}

export function useOnboardingRecords(): UseOnboardingResult {
  const [records, setRecords] = useState<OnboardingRecord[]>([]);
  const [ownerNames, setOwnerNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [partRes, empRes] = await Promise.all([
          supabase
            .from('partner_onboarding')
            .select(
              // ⛔ coi_expiration_date is NOT selected. partner_onboarding's copy
              // is retired (partner_documents is the single source) and nothing
              // outside this hook ever read the field it fed.
              'onboarding_id, provider_id, status, checklist_json, contract_link, w9_link, completed_date, owner_position_key, assigned_to_eid, notes, created_at, updated_at',
            ),
          supabase
            .from('employee_onboarding')
            .select(
              'onboarding_id, employee_id, hiring_request_id, position_id, start_date, employment_type, status, checklist_json, completed_date, assigned_am, owner_position_key, assigned_to_eid, notes, created_at, updated_at',
            ),
        ]);
        if (partRes.error) throw partRes.error;
        if (empRes.error) throw empRes.error;

        type PartRow = {
          onboarding_id: string;
          provider_id: string | null;
          status: string | null;
          checklist_json: Record<string, unknown> | null;
          contract_link: string | null;
          w9_link: string | null;
          completed_date: string | null;
          owner_position_key: string | null;
          assigned_to_eid: string | null;
          notes: string | null;
          created_at: string | null;
          updated_at: string | null;
        };
        type EmpRow = {
          onboarding_id: string;
          employee_id: string | null;
          hiring_request_id: string | null;
          position_id: string | null;
          start_date: string | null;
          employment_type: string | null;
          status: string | null;
          checklist_json: Record<string, unknown> | null;
          completed_date: string | null;
          assigned_am: string | null;
          owner_position_key: string | null;
          assigned_to_eid: string | null;
          notes: string | null;
          created_at: string | null;
          updated_at: string | null;
        };
        const partRows = (partRes.data ?? []) as PartRow[];
        const empRows = (empRes.data ?? []) as EmpRow[];

        // ----- Resolve join lookups --------------------------------------
        const providerIds = [...new Set(partRows.map((r) => r.provider_id).filter(Boolean) as string[])];
        const employeeIds = [...new Set(empRows.map((r) => r.employee_id).filter(Boolean) as string[])];
        const positionIds = [...new Set(empRows.map((r) => r.position_id).filter(Boolean) as string[])];

        const providerMap = await fetchMap<{
          provider_id: string;
          partner_name: string | null;
          worker_class: string | null;
          coverage_markets: string[] | null;
        }>('service_providers', 'provider_id', providerIds, 'provider_id, partner_name, worker_class, coverage_markets');

        const employeeMap = await fetchMap<{
          employee_id: string;
          employee_name_first: string | null;
          employee_name_last: string | null;
        }>('employees', 'employee_id', employeeIds, 'employee_id, employee_name_first, employee_name_last');

        const positionMap = await fetchMap<{
          position_id: string;
          title: string | null;
          account_id: string | null;
        }>('positions', 'position_id', positionIds, 'position_id, title, account_id');

        const posAccountIds = [
          ...new Set(Object.values(positionMap).map((p) => p.account_id).filter(Boolean) as string[]),
        ];

        // Partner active-account aggregation: active assignments → facility →
        // account. service_assignments has no account_id; we derive via facility.
        const asgRes = providerIds.length
          ? await supabase
              .from('service_assignments')
              .select('provider_id, facility_id, assignment_status')
              .in('provider_id', providerIds)
              .eq('assignment_status', 'Active')
          : { data: [], error: null };
        const asgRows = (asgRes.data ?? []) as {
          provider_id: string | null;
          facility_id: string | null;
        }[];
        const asgFacilityIds = [...new Set(asgRows.map((a) => a.facility_id).filter(Boolean) as string[])];
        const facMap = await fetchMap<{ facility_id: string; account_id: string | null }>(
          'facilities',
          'facility_id',
          asgFacilityIds,
          'facility_id, account_id',
        );
        const allAccountIds = [
          ...new Set([
            ...posAccountIds,
            ...Object.values(facMap).map((f) => f.account_id).filter(Boolean) as string[],
          ]),
        ];
        const accountMap = await fetchMap<{ account_id: string; account_name: string | null }>(
          'accounts',
          'account_id',
          allAccountIds,
          'account_id, account_name',
        );

        // provider_id → { count, sampleAccountName }
        const providerAccounts: Record<string, Set<string>> = {};
        for (const a of asgRows) {
          if (!a.provider_id || !a.facility_id) continue;
          const acct = facMap[a.facility_id]?.account_id;
          if (!acct) continue;
          (providerAccounts[a.provider_id] ??= new Set()).add(acct);
        }

        // ----- Build unified records -------------------------------------
        const partnerRecords: OnboardingRecord[] = partRows.map((r) => {
          const prov = r.provider_id ? providerMap[r.provider_id] : undefined;
          const type = partnerTypeToOnboardingType(prov?.worker_class ?? null);
          const sm = mapStatusToStage(r.status);
          const checklist = parseChecklist(r.checklist_json);
          const acctSet = r.provider_id ? providerAccounts[r.provider_id] : undefined;
          let account: string | null = (prov?.coverage_markets ?? []).join(' · ') || null;
          if (acctSet && acctSet.size > 0) {
            const first = [...acctSet][0];
            const name = accountMap[first]?.account_name ?? first;
            account = acctSet.size > 1 ? `${name} +${acctSet.size - 1}` : name;
          }
          return {
            onboarding_id: r.onboarding_id,
            source: 'partner',
            type,
            employmentTypeForChecklist: type,
            name: prov?.partner_name ?? r.provider_id ?? '(unnamed)',
            role: payeeClassLabel(prov?.worker_class ?? null),
            account,
            start_date: null,
            rawStatus: r.status,
            stage: sm.stage,
            terminal: sm.terminal,
            checklist,
            rawChecklistJson: r.checklist_json,
            owner_position_key: r.owner_position_key,
            assigned_to_eid: r.assigned_to_eid,
            assigned_am: null,
            notes: r.notes,
            created_at: r.created_at,
            updated_at: r.updated_at,
            hiring_request_id: null,
            position_id: null,
            employee_id: null,
            provider_id: r.provider_id,
            w9_link: r.w9_link,
          };
        });

        const employeeRecords: OnboardingRecord[] = empRows.map((r) => {
          const type = empTypeToOnboardingType(r.employment_type);
          const sm = mapStatusToStage(r.status);
          const checklist = parseChecklist(r.checklist_json);
          const emp = r.employee_id ? employeeMap[r.employee_id] : undefined;
          const pos = r.position_id ? positionMap[r.position_id] : undefined;
          const acctName = pos?.account_id ? accountMap[pos.account_id]?.account_name ?? null : null;
          const name = emp
            ? `${emp.employee_name_first ?? ''} ${emp.employee_name_last ?? ''}`.trim() || '(unnamed)'
            : pos?.title
              ? `New hire — ${pos.title}`
              : '(unnamed)';
          return {
            onboarding_id: r.onboarding_id,
            source: 'employee',
            type,
            employmentTypeForChecklist: type,
            name,
            role: pos?.title ?? null,
            account: acctName,
            start_date: r.start_date,
            rawStatus: r.status,
            stage: sm.stage,
            terminal: sm.terminal,
            checklist,
            rawChecklistJson: r.checklist_json,
            owner_position_key: r.owner_position_key,
            assigned_to_eid: r.assigned_to_eid,
            assigned_am: r.assigned_am,
            notes: r.notes,
            created_at: r.created_at,
            updated_at: r.updated_at,
            hiring_request_id: r.hiring_request_id,
            position_id: r.position_id,
            employee_id: r.employee_id,
            provider_id: null,
            w9_link: null,
          };
        });

        // TCSD-0162 — resolve the optional named assignee through v_am_name, the
        // canonical resolver every role can read (employees RLS is restrictive).
        // ⛔ The name is for DISPLAY beside the position; it is never the owner.
        const assigneeEids = [...employeeRecords, ...partnerRecords]
          .map((r) => r.assigned_to_eid)
          .filter((e): e is string => !!e);
        const resolved = await fetchAmNameMap(assigneeEids);

        if (cancelled) return;
        setOwnerNames(resolved);
        setRecords([...employeeRecords, ...partnerRecords]);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(errorMessage(e, String(e)));
          setRecords([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  const patchRecord = useCallback<UseOnboardingResult['patchRecord']>(
    async (rec, dbPatch, localPatch) => {
      let snapshot: OnboardingRecord | null = null;
      setRecords((rows) =>
        rows.map((r) => {
          if (r.onboarding_id !== rec.onboarding_id || r.source !== rec.source) return r;
          snapshot = r;
          return { ...r, ...localPatch };
        }),
      );
      try {
        await patchOnboarding(rec.source, rec.onboarding_id, dbPatch);
      } catch (err) {
        if (snapshot) {
          const snap = snapshot;
          setRecords((rows) =>
            rows.map((r) =>
              r.onboarding_id === rec.onboarding_id && r.source === rec.source ? snap : r,
            ),
          );
        }
        throw err;
      }
    },
    [],
  );

  return { records, ownerNames, loading, error, refetch, patchRecord };
}

// One onboarding card's save — the detail panel's stage move, checklist, note,
// archive and restore, the board's drag, and Ask AI's onboarding card. A save
// the database turns away (no row changed) is an error, never a silent no-op.
export async function patchOnboarding(
  source: OnboardingRecord['source'],
  onboardingId: string,
  dbPatch: Record<string, unknown>,
): Promise<void> {
  const table = source === 'employee' ? 'employee_onboarding' : 'partner_onboarding';
  await commitWrite({
    label: 'save the onboarding card',
    write: supabase.from(table).update(dbPatch).eq('onboarding_id', onboardingId).select('onboarding_id'),
    touches: [table],
  });
}

// Convenience: progress for a record using its effective checklist.
export function recordProgress(rec: OnboardingRecord): number {
  return computeProgress(effectiveChecklist(rec.checklist), rec.employmentTypeForChecklist);
}

// ---------------------------------------------------------------------------
// Onboarding → Employee (H2, Antonela 2026-06-02).
// Turns an employee-track onboarding record into a real employees row and links
// it back (employee_onboarding.employee_id). Deliberately does NOT mark the
// onboarding terminal: an active employee and an in-progress onboarding can
// coexist (the person may already be working before every checklist item is
// done). employee_id is generated client-side — there is no insert trigger on
// employees (verified 2026-06-02; max was EID-0032).
// ---------------------------------------------------------------------------
async function nextEmployeeId(): Promise<string> {
  const { data, error } = await supabase
    .from('employees')
    .select('employee_id')
    .order('employee_id', { ascending: false })
    .limit(1);
  if (error) throw error;
  const last = (data?.[0]?.employee_id as string | undefined) ?? null;
  const n = last ? parseInt(last.replace(/\D/g, ''), 10) || 0 : 0;
  return `EID-${String(n + 1).padStart(4, '0')}`;
}

// Best-effort extraction of the hire's name from the handoff-stamped notes
// ("… Hire: Jane Doe."). Used only to PRE-FILL the confirm dialog; HR confirms.
export function parseHireNameFromNotes(notes: string | null): { first: string; last: string } {
  if (!notes) return { first: '', last: '' };
  const m = notes.match(/Hire:\s*([^.\n]+)/i);
  const full = (m?.[1] ?? '').trim();
  if (!full) return { first: '', last: '' };
  const parts = full.split(/\s+/);
  return { first: parts[0] ?? '', last: parts.slice(1).join(' ') };
}

export interface CreateEmployeeFromOnboardingInput {
  firstName: string;
  lastName: string;
  employmentType: string; // 'W2' | 'Subcontractor'
}

export async function createEmployeeFromOnboarding(
  rec: OnboardingRecord,
  input: CreateEmployeeFromOnboardingInput,
): Promise<{ employee_id: string }> {
  if (rec.source !== 'employee') {
    throw new Error('Only employee-track onboarding records become employees.');
  }
  if (rec.employee_id) {
    throw new Error(`Already linked to employee ${rec.employee_id}.`);
  }
  const employeeId = await nextEmployeeId();

  // Carry facility forward from the linked position when present.
  let primaryFacilityId: string | null = null;
  if (rec.position_id) {
    const { data: pos } = await supabase
      .from('positions')
      .select('facility_id')
      .eq('position_id', rec.position_id)
      .maybeSingle();
    primaryFacilityId = (pos?.facility_id as string | null) ?? null;
  }

  const { error: insErr } = await supabase.from('employees').insert({
    employee_id: employeeId,
    employee_name_first: input.firstName.trim() || null,
    employee_name_last: input.lastName.trim() || null,
    employment_type: input.employmentType,
    employment_status: 'Active',
    hire_date: rec.start_date,
    position_id: rec.position_id,
    primary_facility_id: primaryFacilityId,
    notes: `Created from onboarding ${rec.onboarding_id}${
      rec.hiring_request_id ? ` (hiring ${rec.hiring_request_id})` : ''
    }.`,
  });
  if (insErr) throw insErr;

  const { error: linkErr } = await supabase
    .from('employee_onboarding')
    .update({ employee_id: employeeId })
    .eq('onboarding_id', rec.onboarding_id);
  if (linkErr) throw linkErr;

  return { employee_id: employeeId };
}

// ---------------------------------------------------------------------------
// useSubcontractors — service_providers ⨝ partner_onboarding + active accts.
// ---------------------------------------------------------------------------
export interface SubcontractorRow {
  provider_id: string;
  onboarding_id: string | null;
  company: string | null;
  /** Legal business name — appears on the contract. Shown for completion;
      collected by Antonella's onboarding when not set at create. */
  legal_business_name: string | null;
  /** Go-by owner (person) — searchable on the card grid. */
  owner_name: string | null;
  provider_status: string | null;
  worker_class: string | null;
  email: string | null;
  phone: string | null;
  market_region: string | null;
  w9_link: string | null;
  coi_expiration_date: string | null;
  coi_light: ReturnType<typeof coiTrafficLight>;
  active_accounts: number;
  /** Count of Active service_assignments (distinct from active_accounts, which
      dedups to distinct client accounts). Shown on the card. */
  active_assignments: number;
  /** Derived capability tags (service_type → Cleaning/Floor/Carpet/…; 2026-07-10). */
  capabilities: Capability[];
  /** Raw distinct service_type values (Active assignments ∪ projects) — feeds
      the "Does: …" line and is searchable. */
  raw_service_types: string[];
  notes: string | null;
  rawChecklistJson: Record<string, unknown> | null;
  /** New Account SOPs linked to this sub (service_provider_id FK). Antonella
      reads the planned start from the SOP — this is the linkage surface
      (Stage 3, 2026-06-09). Newest first. */
  linked_sops: LinkedSop[];
}

export interface LinkedSop {
  sop_id: string;
  account_name: string | null;
  start_date: string | null;
  stage: string | null;
}

export interface UseSubcontractorsResult {
  rows: SubcontractorRow[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  patchSub: (
    row: SubcontractorRow,
    dbPatch: Record<string, unknown>,
    localPatch: Partial<SubcontractorRow>,
  ) => Promise<void>;
}

export function useSubcontractors(): UseSubcontractorsResult {
  const [rows, setRows] = useState<SubcontractorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const provRes = await supabase
          .from('service_providers')
          // provider_class split (2026-06-16): onboarding / W-9 / COI tracking
          // only applies to field-service subs. Exclude back-office/staffing/
          // professional-services/supply vendors from the onboarding board.
          .select('provider_id, partner_name, legal_business_name, owner_name, worker_class, coverage_markets, email, phone, provider_status')
          .eq('provider_class', 'Field_Service')
          // Dedup (2026-07-10): 'Merged' stubs never render. 'Inactive' is
          // default-hidden in the page with a "Show inactive" toggle (kept in
          // the result set here so the toggle can reveal them without a refetch).
          .neq('provider_status', 'Merged')
          .order('partner_name', { ascending: true });
        if (provRes.error) throw provRes.error;
        const provs = (provRes.data ?? []) as {
          provider_id: string;
          partner_name: string | null;
          legal_business_name: string | null;
          owner_name: string | null;
          worker_class: string | null;
          coverage_markets: string[] | null;
          email: string | null;
          phone: string | null;
          provider_status: string | null;
        }[];

        const onbRes = await supabase
          .from('partner_onboarding')
          // ⛔ coi_expiration_date is NOT selected — it was already dead here:
          // the COI expiry on this row comes from partner_documents below.
          .select('onboarding_id, provider_id, w9_link, checklist_json, notes');
        const onbByProvider: Record<string, {
          onboarding_id: string;
          w9_link: string | null;
          checklist_json: Record<string, unknown> | null;
          notes: string | null;
        }> = {};
        for (const o of (onbRes.data ?? []) as {
          onboarding_id: string;
          provider_id: string | null;
          w9_link: string | null;
          checklist_json: Record<string, unknown> | null;
          notes: string | null;
        }[]) {
          if (o.provider_id) onbByProvider[o.provider_id] = o;
        }

        const providerIds = provs.map((p) => p.provider_id);

        // W9 / COI now come from partner_documents (canonical, 2026-07-10) — no
        // longer from partner_onboarding.w9_link / checklist_json.coi_link. We
        // derive the row's W9 link + COI expiry from the doc rows so the list's
        // W9 column, "Missing W9" filter, and COI traffic light all read the
        // single source of truth. A W9/COI counts as "on file" only when it has
        // a drive_link and is not expired.
        const docRes = providerIds.length
          ? await supabase
              .from('partner_documents')
              .select('provider_id, document_type, drive_link, expiration_date')
              .in('provider_id', providerIds)
              .in('document_type', ['W9', 'COI'])
          : { data: [], error: null };
        const today = new Date().toISOString().slice(0, 10);
        const docByProvider: Record<string, { w9_link: string | null; coi_exp: string | null }> = {};
        for (const d of (docRes.data ?? []) as {
          provider_id: string | null;
          document_type: string | null;
          drive_link: string | null;
          expiration_date: string | null;
        }[]) {
          if (!d.provider_id) continue;
          const notExpired = !d.expiration_date || d.expiration_date >= today;
          const slot = (docByProvider[d.provider_id] ??= { w9_link: null, coi_exp: null });
          if (d.document_type === 'W9' && d.drive_link && notExpired) slot.w9_link = d.drive_link;
          // COI expiry drives the traffic light; take the latest expiry on file.
          if (d.document_type === 'COI') {
            if (!slot.coi_exp || (d.expiration_date && d.expiration_date > slot.coi_exp)) {
              slot.coi_exp = d.expiration_date ?? slot.coi_exp;
            }
          }
        }

        // Active accounts + active-assignment count + capability labels per
        // provider (via facility → account). The assignment names its work once,
        // from the one list, so the catalogue label rides along here and the
        // card grid derives capability tags without a second round-trip.
        const asgRes = providerIds.length
          ? await supabase
              .from('service_assignments')
              .select('provider_id, facility_id, assignment_status, service_code, service_catalog(display_name)')
              .in('provider_id', providerIds)
              .eq('assignment_status', 'Active')
          : { data: [], error: null };
        const asgRows = (asgRes.data ?? []) as unknown as {
          provider_id: string | null;
          facility_id: string | null;
          service_catalog: { display_name: string | null }[] | { display_name: string | null } | null;
        }[];
        const facIds = [...new Set(asgRows.map((a) => a.facility_id).filter(Boolean) as string[])];
        const facMap = await fetchMap<{ facility_id: string; account_id: string | null }>(
          'facilities',
          'facility_id',
          facIds,
          'facility_id, account_id',
        );
        const provAccts: Record<string, Set<string>> = {};
        const provAsgCount: Record<string, number> = {};
        const provServiceTypes: Record<string, Set<string>> = {};
        for (const a of asgRows) {
          if (!a.provider_id) continue;
          provAsgCount[a.provider_id] = (provAsgCount[a.provider_id] ?? 0) + 1;
          const asgLabel = catalogLabel(a.service_catalog);
          if (asgLabel) (provServiceTypes[a.provider_id] ??= new Set()).add(asgLabel);
          if (!a.facility_id) continue;
          const acct = facMap[a.facility_id]?.account_id;
          if (!acct) continue;
          (provAccts[a.provider_id] ??= new Set()).add(acct);
        }

        // Capability also draws from project work the sub has done. TCSD-0380
        // (item #680): each service line names its own sub and its own service from
        // the catalogue, so this reads the LINES — the project's free-text service
        // and its single subcontractor slot are no longer the source. A chargeback
        // line names a sub being charged, not work done, so it is skipped.
        if (providerIds.length) {
          const lineRes = await supabase
            .from('project_services')
            .select('assigned_partner_id, line_role, service_catalog(display_name)')
            .in('assigned_partner_id', providerIds)
            .eq('line_role', 'service')
            // "Other - describe it" names no service, so it says nothing about capability.
            .neq('service_code', 'OTHER');
          for (const l of (lineRes.data ?? []) as unknown as {
            assigned_partner_id: string | null;
            service_catalog: { display_name: string | null }[] | { display_name: string | null } | null;
          }[]) {
            const label = catalogLabel(l.service_catalog);
            if (!l.assigned_partner_id || !label) continue;
            (provServiceTypes[l.assigned_partner_id] ??= new Set()).add(label);
          }
        }

        // Linked New Account SOPs per provider — the sub ↔ SOP linkage surface.
        // Flat fetch + client-join (standard pattern, no PostgREST embed).
        // ⛔ THE LINK IS THROUGH THE BUILDING, NOT THE CARD'S OWN COLUMN.
        // This used to read new_account_sop.service_provider_id — the card's
        // copy of who cleans the building. That column holds one sub for a
        // whole card, so a sub on a second line was invisible, and it stopped
        // being written at all when the building became the record. The sub is
        // on the building's SERVICE LINE; the card is the building's onboarding
        // card. Two hops, both of them real.
        const sopsByProvider: Record<string, LinkedSop[]> = {};
        if (providerIds.length) {
          const asnRes = await supabase
            .from('service_assignments')
            .select('facility_id, provider_id')
            .in('provider_id', providerIds)
            .in('assignment_status', ['Active', 'Scheduled']);
          if (asnRes.error) throw asnRes.error;

          const providersByFacility = new Map<string, Set<string>>();
          for (const a of (asnRes.data ?? []) as Array<{ facility_id: string | null; provider_id: string | null }>) {
            if (!a.facility_id || !a.provider_id) continue;
            (providersByFacility.get(a.facility_id) ?? providersByFacility.set(a.facility_id, new Set()).get(a.facility_id)!)
              .add(a.provider_id);
          }

          if (providersByFacility.size) {
            const sopRes = await supabase
              .from('new_account_sop')
              .select('sop_id, account_name, start_date, stage, facility_id')
              .in('facility_id', [...providersByFacility.keys()])
              .neq('status', 'Cancelled')
              .order('created_date', { ascending: false });
            if (sopRes.error) throw sopRes.error;
            for (const s of (sopRes.data ?? []) as Array<LinkedSop & { facility_id: string | null }>) {
              if (!s.facility_id) continue;
              for (const pid of providersByFacility.get(s.facility_id) ?? []) {
                (sopsByProvider[pid] ??= []).push({
                  sop_id: s.sop_id,
                  account_name: s.account_name,
                  start_date: s.start_date,
                  stage: s.stage,
                });
              }
            }
          }
        }

        const built: SubcontractorRow[] = provs.map((p) => {
          const onb = onbByProvider[p.provider_id];
          const serviceTypes = [...(provServiceTypes[p.provider_id] ?? new Set<string>())];
          return {
            provider_id: p.provider_id,
            onboarding_id: onb?.onboarding_id ?? null,
            company: p.partner_name,
            legal_business_name: p.legal_business_name,
            owner_name: p.owner_name,
            provider_status: p.provider_status,
            worker_class: p.worker_class,
            email: p.email,
            phone: p.phone,
            // The markets the sub covers, read as one line (item #683 — the typed field is retired).
            market_region: (p.coverage_markets ?? []).join(' · ') || null,
            // Canonical W9/COI source is partner_documents (2026-07-10).
            w9_link: docByProvider[p.provider_id]?.w9_link ?? null,
            coi_expiration_date: docByProvider[p.provider_id]?.coi_exp ?? null,
            coi_light: coiTrafficLight(docByProvider[p.provider_id]?.coi_exp ?? null),
            active_accounts: provAccts[p.provider_id]?.size ?? 0,
            active_assignments: provAsgCount[p.provider_id] ?? 0,
            capabilities: deriveCapabilities(serviceTypes),
            raw_service_types: serviceTypes,
            notes: onb?.notes ?? null,
            rawChecklistJson: onb?.checklist_json ?? null,
            linked_sops: sopsByProvider[p.provider_id] ?? [],
          };
        });
        if (cancelled) return;
        setRows(built);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(errorMessage(e, String(e)));
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  const patchSub = useCallback<UseSubcontractorsResult['patchSub']>(
    async (row, dbPatch, localPatch) => {
      if (!row.onboarding_id) {
        throw new Error('No partner_onboarding record for this provider yet.');
      }
      let snapshot: SubcontractorRow | null = null;
      setRows((rs) =>
        rs.map((r) => {
          if (r.provider_id !== row.provider_id) return r;
          snapshot = r;
          return { ...r, ...localPatch };
        }),
      );
      try {
        const { error: e } = await supabase
          .from('partner_onboarding')
          .update(dbPatch)
          .eq('onboarding_id', row.onboarding_id);
        if (e) throw e;
      } catch (err) {
        if (snapshot) {
          const snap = snapshot;
          setRows((rs) => rs.map((r) => (r.provider_id === row.provider_id ? snap : r)));
        }
        throw err;
      }
    },
    [],
  );

  return { rows, loading, error, refetch, patchSub };
}

// ---------------------------------------------------------------------------
// useBirthdaysAnniversaries — employees with dob/hire_date in the given month.
// employees.dob + employees.hire_date both exist (verified 2026-05-28), so
// these resolve to real data rather than the prompt's field-missing fallback.
// ---------------------------------------------------------------------------
export interface PeopleDateRow {
  employee_id: string;
  name: string;
  date: string; // ISO
  day: number; // day-of-month, for sorting
}

export interface UsePeopleDatesResult {
  birthdays: PeopleDateRow[];
  anniversaries: PeopleDateRow[];
  loading: boolean;
  error: string | null;
  dobFieldMissing: boolean;
  hireFieldMissing: boolean;
}

export function usePeopleDates(): UsePeopleDatesResult {
  const [state, setState] = useState<UsePeopleDatesResult>({
    birthdays: [],
    anniversaries: [],
    loading: true,
    error: null,
    dobFieldMissing: false,
    hireFieldMissing: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await supabase
        .from('employees')
        .select('employee_id, employee_name_first, employee_name_last, dob, hire_date, employment_status');
      if (cancelled) return;
      if (res.error) {
        setState((s) => ({ ...s, loading: false, error: res.error!.message }));
        return;
      }
      const month = new Date().getUTCMonth() + 1;
      const rows = ((res.data ?? []) as {
        employee_id: string;
        employee_name_first: string | null;
        employee_name_last: string | null;
        dob: string | null;
        hire_date: string | null;
        employment_status: string | null;
      }[]).filter((e) => !['Terminated', 'Separated'].includes(e.employment_status ?? ''));

      const name = (e: { employee_name_first: string | null; employee_name_last: string | null }) =>
        `${e.employee_name_first ?? ''} ${e.employee_name_last ?? ''}`.trim() || '(unnamed)';
      const inMonth = (d: string | null) =>
        !!d && new Date(d + 'T00:00:00Z').getUTCMonth() + 1 === month;
      const dayOf = (d: string) => new Date(d + 'T00:00:00Z').getUTCDate();

      const birthdays = rows
        .filter((e) => inMonth(e.dob))
        .map((e) => ({ employee_id: e.employee_id, name: name(e), date: e.dob!, day: dayOf(e.dob!) }))
        .sort((a, b) => a.day - b.day);
      const anniversaries = rows
        .filter((e) => inMonth(e.hire_date))
        .map((e) => ({ employee_id: e.employee_id, name: name(e), date: e.hire_date!, day: dayOf(e.hire_date!) }))
        .sort((a, b) => a.day - b.day);

      setState({
        birthdays,
        anniversaries,
        loading: false,
        error: null,
        dobFieldMissing: false,
        hireFieldMissing: false,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}


// ---------------------------------------------------------------------------
// useOnboardingTrend — "+N new in the last 7 days" for the dashboard tile.
// This is the only honestly-computable trend without a historical snapshot
// table: we count rows created in the trailing week. W9 / COI tiles can't be
// trended at week granularity (no point-in-time history) — flagged in report.
// ---------------------------------------------------------------------------
export function useOnboardingTrend(): { new7d: number | null } {
  const [new7d, setNew7d] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cutoff = new Date(Date.now() - 7 * 86_400_000).toISOString();
      try {
        const [p, e] = await Promise.all([
          supabase
            .from('partner_onboarding')
            .select('onboarding_id', { count: 'exact', head: true })
            .gte('created_at', cutoff),
          supabase
            .from('employee_onboarding')
            .select('onboarding_id', { count: 'exact', head: true })
            .gte('created_at', cutoff),
        ]);
        if (cancelled) return;
        setNew7d((p.count ?? 0) + (e.count ?? 0));
      } catch {
        if (!cancelled) setNew7d(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return { new7d };
}
